import { MenuItem, TextField, Typography, styled } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { type $Plate, MetalworksTokenMissingError } from 'metalworks-domain'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import { useDomainEntityState } from '@salsita-ai/platform-project-sdk/react'
import { ListSelector, Loader, TextInput } from '@salsita-ai/platform-project-sdk/ui'

import { MetalworksAnalytics, analyticsService } from '../../../systems/analytics'
import { configuratorInterface } from '../../../systems/configurator-interface'
import {
  clearQuoteId,
  clearQuoteLineId,
  getQuoteId,
  getQuoteLineId,
  useMetalworksApi,
} from '../../../systems/metalworks-api'

export type FinalizationType = 'new' | 'existingQuote' | 'existingQuoteLine'
export interface FinalizationFormData {
  finalizationType: FinalizationType
  quoteId: number | null
  quoteLineId: number | null
  theirReference: string
  usage: string
}

const formSchema = z.object({
  finalizationType: z.union([z.literal('new'), z.literal('existingQuote'), z.literal('existingQuoteLine')]),
  quoteId: z.number().nullable(),
  theirReference: z.string(),
  usage: z
    .string()
    .optional()
    .transform(value => value ?? ''),
})

interface Inputs {
  finalizationType: FinalizationType
  quoteId: number | null
  theirReference: string
  usage: string
}

const FORM_ID = 'finalize-submit-button'

const Form = styled('form')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(1, 1fr)',
  rowGap: theme.spacing(3),
  alignItems: 'center',
}))

interface JobDetailsFormProps {
  formId?: string
  disableQuoteLineEdit?: boolean
  onSubmit: (data: FinalizationFormData) => void
}

export const JobDetailsForm = (props: JobDetailsFormProps) => {
  const { onSubmit, disableQuoteLineEdit = false, formId = FORM_ID } = props

  const metalworksApi = useMetalworksApi()

  const { storeSystem, configurationId, rootEntityId } = configuratorInterface.useDomain()
  const $plate = useDomainEntityState(rootEntityId) as $Plate

  const [quoteId, setQuoteId] = useState<number | null>(getQuoteId(configurationId))
  const [quoteLineId, setQuoteLineId] = useState<number | null>(
    disableQuoteLineEdit ? null : getQuoteLineId(configurationId)
  )

  // Fetch list of available quotes from Daveweb - user can pick one of them and add the configuration to it.
  const { data: quoteListResponse, isLoading: isLoadingQuoteList } = useQuery({
    queryKey: ['quotes', 'list'],
    queryFn: () => {
      return metalworksApi.listQuotes()
    },
    retry: (_count, err) => !(err instanceof MetalworksTokenMissingError),
  })

  const quoteOptions = useMemo(() => {
    return (
      quoteListResponse?.results
        .filter(quote => quote.isCurrent)
        .map(quote => ({
          theirReference: quote.theirReference,
          quoteId: quote.id,
        })) ?? []
    )
  }, [quoteListResponse])

  const { data: quoteResponse, isLoading: isLoadingQuote } = useQuery({
    enabled: quoteId !== null,
    queryKey: ['quote', quoteId],
    queryFn: () => {
      return quoteId !== null ? metalworksApi.getQuote({ quoteId }) : null
    },
    retry: (_count, err) => !(err instanceof MetalworksTokenMissingError),
  })

  // Fetch details of the selected quote line from Daveweb
  const { data: quoteLineResponse, isLoading: isLoadingQuoteLine } = useQuery({
    enabled: quoteId !== null && quoteLineId !== null,
    queryKey: ['quoteLine', quoteLineId],
    queryFn: () => {
      return quoteId !== null && quoteLineId !== null ? metalworksApi.getQuoteLine({ quoteId, quoteLineId }) : null
    },
    retry: (_count, err) => !(err instanceof MetalworksTokenMissingError),
  })

  // If current quote is not editable, unlink it from the configuration.
  useEffect(() => {
    if (quoteResponse?.isCurrent === false) {
      setQuoteId(null)
      clearQuoteId(configurationId)
    }
  }, [quoteResponse?.isCurrent, configurationId])

  // If current quote line is not editable, unlink it from the configuration.
  useEffect(() => {
    if (quoteLineResponse?.canUpdate === false) {
      setQuoteId(null)
      setQuoteLineId(null)
      clearQuoteId(configurationId)
      clearQuoteLineId(configurationId)
    }
  }, [quoteLineResponse?.canUpdate, configurationId])

  const finalizationTypeDefault =
    quoteLineId !== null ? 'existingQuoteLine' : quoteId !== null ? 'existingQuote' : 'new'

  const { handleSubmit, control, watch, setValue } = useForm<Inputs>({
    defaultValues: {
      theirReference: '',
      usage: $plate.usage,
      quoteId,
      finalizationType: finalizationTypeDefault,
    },
  })

  // Set finalization type based on current "editable" state of quote and quote line.
  // `quoteId` and `quoteLineId` are updated in the `useEffect` hooks above when current
  // quote and quote line data are fetched from Daveweb.
  useEffect(() => {
    setValue('finalizationType', finalizationTypeDefault)
  }, [finalizationTypeDefault, setValue])

  const [usage, finalizationType] = watch(['usage', 'finalizationType'])

  useEffect(() => {
    if (quoteListResponse?.results.length === 0) {
      setValue('finalizationType', 'new')
    }
  }, [quoteListResponse, setValue])

  useEffect(() => {
    if (usage !== $plate.usage) {
      storeSystem.stageAndCommit([
        configuratorInterface.updates.DomainEntityModification({
          entityId: rootEntityId,
          stateDelta: {
            usage,
          },
          updateTag: 'updateUsage',
        }),
      ])
    }
  }, [$plate, usage, storeSystem, rootEntityId])

  const validateSubmit = useCallback(
    (data: unknown) => {
      const { finalizationType, quoteId, theirReference, usage } = formSchema.parse(data)

      analyticsService.push(MetalworksAnalytics.ProceedToQuoteButtonClicked(finalizationType))

      onSubmit({
        finalizationType,
        theirReference,
        usage,
        quoteId: finalizationType === 'new' ? null : quoteId,
        quoteLineId: finalizationType === 'existingQuoteLine' ? quoteLineId : null,
      })
    },
    [onSubmit, quoteLineId]
  )

  if (isLoadingQuoteList || isLoadingQuote || isLoadingQuoteLine) {
    return <Loader />
  }

  return (
    <>
      <Typography variant="h4" gutterBottom id="scenario-detail-dialog-title" sx={{ mb: 5 }}>
        Enter Your Job Details
      </Typography>
      <Form onSubmit={handleSubmit(validateSubmit)} id={formId}>
        {quoteOptions.length > 0 && (
          <Controller
            name="finalizationType"
            control={control}
            render={({ field }) => (
              <ListSelector.Root
                value={field.value}
                onChange={field.onChange}
                direction={quoteLineId === null ? 'horizontal' : 'vertical'}
              >
                <ListSelector.Item title="Create New Quote" value="new" />
                <ListSelector.Item title="Add to Existing Quote" value="existingQuote" />
                {quoteLineId !== null && (
                  <ListSelector.Item title="Update Existing Quote Line" value="existingQuoteLine" />
                )}
              </ListSelector.Root>
            )}
          />
        )}

        <div style={{ display: finalizationType === 'new' ? 'block' : 'none' }}>
          <Controller
            name="theirReference"
            control={control}
            rules={finalizationType === 'new' ? { required: 'This field is required' } : { required: false }}
            render={({ field, fieldState }) => (
              <TextInput
                placeholder="Customer's Order #"
                hasError={!!fieldState.error}
                helperText={
                  fieldState.error?.message ??
                  'Provide your order number or any other identifier for the configuration. This field is required.'
                }
                {...field}
              />
            )}
          />
        </div>

        {finalizationType === 'existingQuote' && (
          <Controller
            name="quoteId"
            control={control}
            rules={{ required: 'This field is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                variant="filled"
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? 'Select existing quote. This is required.'}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
              >
                {quoteOptions.map(option => (
                  <MenuItem key={option.quoteId} value={option.quoteId}>
                    {option.theirReference}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}

        {finalizationType === 'existingQuoteLine' && (
          <TextInput value={quoteResponse?.theirReference ?? '(could not load quote details)'} disabled />
        )}

        <Controller
          name="usage"
          control={control}
          render={({ field }) => (
            <TextInput
              placeholder="Suits"
              helperText="Provide any additional information about the usage of the configuration."
              {...field}
              multiline
              rows={5}
            />
          )}
        />
      </Form>
    </>
  )
}

export { FORM_ID }
